import { c as _c } from "react/compiler-runtime";
import { cn } from '@newfront-insurance/core-ui/v2';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { forwardRef } from 'react';
import { useSlashCommandListState } from './use-slash-command-list-state';
import { CATEGORY_DISPLAY_NAMES, CATEGORY_ICONS, type SlashCommand } from '../../../../lib/slash-command-constants';
export type SlashCommandListVariant = 'default' | 'compact';
interface SlashCommandListProps {
  commands: SlashCommand[];
  searchQuery: string;
  isOpen: boolean;
  onSelect: (command: SlashCommand) => void;
  onClose: () => void;
  inputRef?: React.RefObject<HTMLTextAreaElement | HTMLDivElement | null>;
  /**
   * Visual variant:
   * - 'default': Standard sizing for desktop/wide panels (popover)
   * - 'compact': Smaller sizing for narrow panels (e.g., Outlook add-in)
   */
  variant?: SlashCommandListVariant;
  className?: string;
  /**
   * Whether to show the keyboard hints footer
   */
  showKeyboardHints?: boolean;
}

// Variant-specific styles
const styles = {
  default: {
    header: 'bg-steel-100 px-3.5 py-2.5',
    headerText: 'text-steel-400 text-sm font-semibold tracking-[1.2px] uppercase',
    categoryHeader: 'bg-steel-50 px-3.5 py-2.5',
    categoryTitle: 'text-steel-500 text-sm font-semibold',
    categoryCount: 'text-steel-400 text-sm',
    footer: 'bg-steel-50 px-3.5 py-2.5',
    footerText: 'text-steel-400 text-sm',
    listMaxHeight: 'max-h-[400px]',
    // Item styles
    itemContainer: 'hover:bg-steel-50',
    itemContainerSelected: 'bg-steel-100',
    itemContainerExpanded: 'bg-brand-100',
    itemPadding: 'px-3.5 py-2.5 gap-3',
    iconContainer: 'size-9 bg-brand-100',
    iconContainerExpanded: 'border-steel-200 border',
    icon: 'size-[18px] text-brand-400',
    handleText: 'text-steel-500 text-sm font-semibold',
    descriptionText: 'text-steel-400 text-sm',
    chevronSize: 'size-4',
    chevronColor: 'text-steel-400 hover:text-steel-500',
    expandedContainer: 'mx-3.5 mb-2.5 bg-steel-50 border-steel-200',
    expandedLabel: 'text-steel-400 text-sm font-semibold tracking-[1.2px] uppercase',
    expandedContent: 'border-steel-200 bg-white',
    expandedText: 'text-steel-400 text-sm leading-4',
    exampleHandle: 'text-fire-400 font-mono'
  },
  compact: {
    header: 'bg-[#f4f5f6] px-3.5 py-2.5',
    headerText: 'text-[12px] font-semibold tracking-[1.2px] text-[#546a83] uppercase',
    categoryHeader: 'bg-[#f4f5f6] px-3.5 py-2.5',
    categoryTitle: 'text-[12px] font-semibold text-[#262d46]',
    categoryCount: 'text-[12px] text-[#546a83]',
    footer: 'bg-[#f4f5f6] px-3.5 py-2.5',
    footerText: 'text-[12px] text-[#546a83]',
    listMaxHeight: '',
    // No max height for compact - uses flex-1
    // Item styles
    itemContainer: 'bg-white border-b border-[#dee2e7]',
    itemContainerSelected: 'bg-[#f4f5f6]',
    itemContainerExpanded: 'bg-[#f4f8fe]',
    itemPadding: 'px-2.5 py-2.5 gap-2.5 items-start',
    iconContainer: 'size-9 bg-[#f4f8fe]',
    iconContainerExpanded: 'border border-[#bec7d0]',
    icon: 'size-[18px] text-[#0957c3]',
    handleText: 'text-[12px] font-semibold text-[#262d46]',
    descriptionText: 'text-[12px] leading-4 text-[#546a83]',
    chevronSize: 'size-3',
    chevronColor: 'text-[#546a83] hover:text-[#262d46]',
    expandedContainer: 'mx-2.5 mb-2.5 bg-[#fafbfc] border-[#dee2e7]',
    expandedLabel: 'text-[12px] font-semibold tracking-[1.2px] text-[#546a83] uppercase',
    expandedContent: 'border-[#dee2e7] bg-white',
    expandedText: 'text-[12px] leading-4 text-[#546a83]',
    exampleHandle: 'text-[#ff7a7a] font-mono'
  }
} as const;
export function SlashCommandList({
  commands,
  searchQuery,
  isOpen,
  onSelect,
  onClose,
  inputRef,
  variant = 'default',
  className,
  showKeyboardHints = true
}: SlashCommandListProps) {
  const {
    filteredCommands,
    groupedCommands,
    selectedIndex,
    expandedCommandUuid,
    listRef,
    toggleExpanded,
    registerItemRef
  } = useSlashCommandListState({
    commands,
    searchQuery,
    isOpen,
    onSelect,
    onClose,
    inputRef
  });
  if (!isOpen || filteredCommands.length === 0) {
    return null;
  }
  const s = styles[variant];
  let itemIndex = 0;
  return <div className={cn('flex w-full flex-col overflow-hidden', className)}>
      {/* Header */}
      <div className={s.header}>
        <span className={s.headerText}>Available Commands</span>
      </div>

      {/* Command List */}
      <div ref={listRef} className={cn('overflow-y-auto', variant === 'default' ? s.listMaxHeight : 'flex-1')}>
        {groupedCommands.map(group => <div key={group.category}>
            {/* Category Header */}
            <div className={cn('flex items-center justify-between', s.categoryHeader)}>
              <span className={s.categoryTitle}>{CATEGORY_DISPLAY_NAMES[group.category] || group.category}</span>
              <span className={s.categoryCount}>
                {group.commands.length} {group.commands.length === 1 ? 'command' : 'commands'}
              </span>
            </div>

            {/* Commands in category */}
            {group.commands.map(command => {
          const currentIndex = itemIndex;
          const isSelected = currentIndex === selectedIndex;
          const isExpanded = expandedCommandUuid === command.uuid;
          const Icon = CATEGORY_ICONS[command.category] || FileText;
          itemIndex += 1;
          return <SlashCommandItem key={command.uuid} ref={el => registerItemRef(currentIndex, el)} command={command} icon={Icon} isSelected={isSelected} isExpanded={isExpanded} onToggleExpand={() => toggleExpanded(command.uuid)} onClick={() => onSelect(command)} variant={variant} />;
        })}
          </div>)}
      </div>

      {/* Footer - Keyboard Hints */}
      {showKeyboardHints && <div className={s.footer}>
          <span className={s.footerText}>
            <kbd className="rounded px-1 py-0.5">↑↓</kbd> to navigate{' '}
            <kbd className="ml-2 rounded px-1 py-0.5">Tab</kbd>/<kbd className="rounded px-1 py-0.5">Enter</kbd> to
            select <kbd className="ml-2 rounded px-1 py-0.5">Esc</kbd> to cancel
          </span>
        </div>}
    </div>;
}
interface SlashCommandItemProps {
  command: SlashCommand;
  icon: React.ComponentType<{
    className?: string;
  }>;
  isSelected: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onClick: () => void;
  variant: SlashCommandListVariant;
}
const SlashCommandItem = forwardRef<HTMLButtonElement, SlashCommandItemProps>((t0, ref) => {
  const $ = _c(52);
  const {
    command,
    icon: Icon,
    isSelected,
    isExpanded,
    onToggleExpand,
    onClick,
    variant
  } = t0;
  const s = styles[variant];
  const t1 = isExpanded && s.itemContainerExpanded;
  const t2 = isSelected && !isExpanded && s.itemContainerSelected;
  let t3;
  if ($[0] !== s.itemContainer || $[1] !== t1 || $[2] !== t2) {
    t3 = cn("flex w-full flex-col transition-colors", s.itemContainer, t1, t2);
    $[0] = s.itemContainer;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== s.itemPadding) {
    t4 = cn("flex w-full text-left", s.itemPadding);
    $[4] = s.itemPadding;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = isExpanded && s.iconContainerExpanded;
  let t6;
  if ($[6] !== s.iconContainer || $[7] !== t5) {
    t6 = cn("flex shrink-0 items-center justify-center rounded-sm", s.iconContainer, t5);
    $[6] = s.iconContainer;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== Icon || $[10] !== s.icon) {
    t7 = <Icon className={s.icon} />;
    $[9] = Icon;
    $[10] = s.icon;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t6 || $[13] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== command.description || $[16] !== command.handle || $[17] !== isExpanded || $[18] !== onToggleExpand || $[19] !== s.chevronColor || $[20] !== s.chevronSize || $[21] !== s.descriptionText || $[22] !== s.handleText || $[23] !== variant) {
    t9 = <div className="flex min-w-0 flex-1 flex-col gap-1">{variant === "compact" ? <><div className="flex items-center justify-between"><span className={s.handleText}>/{command.handle}</span><button type="button" onClick={e => {
            e.stopPropagation();
            onToggleExpand();
          }} className={cn("shrink-0 p-1", s.chevronColor)} aria-label={isExpanded ? "Collapse command details" : "Expand command details"}>{isExpanded ? <ChevronUp className={s.chevronSize} /> : <ChevronDown className={s.chevronSize} />}</button></div><span className={s.descriptionText}>{command.description}</span></> : <><span className={s.handleText}>/{command.handle}</span><span className={cn(s.descriptionText, "truncate")}>{command.description}</span></>}</div>;
    $[15] = command.description;
    $[16] = command.handle;
    $[17] = isExpanded;
    $[18] = onToggleExpand;
    $[19] = s.chevronColor;
    $[20] = s.chevronSize;
    $[21] = s.descriptionText;
    $[22] = s.handleText;
    $[23] = variant;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== isExpanded || $[26] !== onToggleExpand || $[27] !== s.chevronColor || $[28] !== s.chevronSize || $[29] !== variant) {
    t10 = variant === "default" && <button type="button" onClick={e_0 => {
      e_0.stopPropagation();
      onToggleExpand();
    }} className={cn("shrink-0 p-1", s.chevronColor)} aria-label={isExpanded ? "Collapse command details" : "Expand command details"}>{isExpanded ? <ChevronUp className={s.chevronSize} /> : <ChevronDown className={s.chevronSize} />}</button>;
    $[25] = isExpanded;
    $[26] = onToggleExpand;
    $[27] = s.chevronColor;
    $[28] = s.chevronSize;
    $[29] = variant;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== onClick || $[32] !== ref || $[33] !== t10 || $[34] !== t4 || $[35] !== t8 || $[36] !== t9) {
    t11 = <button ref={ref} type="button" className={t4} onClick={onClick}>{t8}{t9}{t10}</button>;
    $[31] = onClick;
    $[32] = ref;
    $[33] = t10;
    $[34] = t4;
    $[35] = t8;
    $[36] = t9;
    $[37] = t11;
  } else {
    t11 = $[37];
  }
  let t12;
  if ($[38] !== command.exampleUsage || $[39] !== command.handle || $[40] !== command.prompt || $[41] !== isExpanded || $[42] !== s.exampleHandle || $[43] !== s.expandedContainer || $[44] !== s.expandedContent || $[45] !== s.expandedLabel || $[46] !== s.expandedText) {
    t12 = isExpanded && <div className={cn("flex flex-col gap-2.5 rounded-[5px] border p-2", s.expandedContainer)}><div className={s.expandedLabel}>Full prompt</div><div className={cn("rounded-[5px] border p-2", s.expandedContent)}><p className={cn(s.expandedText, "whitespace-pre-wrap")}>{command.prompt}</p></div>{command.exampleUsage && <><div className={s.expandedLabel}>Example usage</div><div className={cn("rounded-[5px] border p-2", s.expandedContent)}><p className={s.expandedText}><span className={s.exampleHandle}>/{command.handle}</span> <span>{command.exampleUsage}</span></p></div></>}</div>;
    $[38] = command.exampleUsage;
    $[39] = command.handle;
    $[40] = command.prompt;
    $[41] = isExpanded;
    $[42] = s.exampleHandle;
    $[43] = s.expandedContainer;
    $[44] = s.expandedContent;
    $[45] = s.expandedLabel;
    $[46] = s.expandedText;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== t11 || $[49] !== t12 || $[50] !== t3) {
    t13 = <div className={t3}>{t11}{t12}</div>;
    $[48] = t11;
    $[49] = t12;
    $[50] = t3;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
});
SlashCommandItem.displayName = 'SlashCommandItem';